// SwimLane.tsx — individual lane row with type badge, field, delete (PRI-291)
// Spec §5, §6, §8

import React from 'react'
import type { Lane, LaneType } from './compiled-query-types'

// Lane accent colours (light mode — V2 forced light mode)
const LANE_COLORS: Record<LaneType, { badgeBg: string; badgeText: string; border: string }> = {
  'SUBJECT':        { badgeBg: 'rgba(27,79,224,0.10)',   badgeText: '#1B4FE0', border: '#1B4FE0' },
  'OBJECT':         { badgeBg: 'rgba(27,79,224,0.10)',   badgeText: '#1B4FE0', border: '#1B4FE0' },
  'ACTION':         { badgeBg: 'rgba(108,40,200,0.10)',  badgeText: '#6D28C8', border: '#6D28C8' },
  'PRESENCE':       { badgeBg: 'rgba(22,163,74,0.10)',   badgeText: '#16A34A', border: '#16A34A' },
  'WHAT TO COUNT':  { badgeBg: 'rgba(249,115,22,0.10)',  badgeText: '#E85C00', border: '#E85C00' },
  'THRESHOLD':      { badgeBg: 'rgba(249,115,22,0.10)',  badgeText: '#E85C00', border: '#E85C00' },
  'ATTRIBUTE':      { badgeBg: 'rgba(108,40,200,0.10)',  badgeText: '#6D28C8', border: '#6D28C8' },
  'LOCATION':       { badgeBg: 'rgba(59,130,246,0.10)',  badgeText: '#2563EB', border: '#2563EB' },
  'STATE':          { badgeBg: 'rgba(217,119,6,0.10)',   badgeText: '#D97706', border: '#D97706' },
  'TEMPORAL':       { badgeBg: 'rgba(122,118,105,0.10)', badgeText: '#7A7669', border: '#7A7669' },
}

interface SwimLaneProps {
  lane: Lane
  editMode: boolean
  isEmpty?: boolean
  onTargetChange?: (value: string) => void
  onDelete?: () => void
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>
  isMobile?: boolean
}

export function SwimLane({
  lane,
  editMode,
  isEmpty = false,
  onTargetChange,
  onDelete,
  dragHandleProps,
  isMobile = false,
}: SwimLaneProps) {
  const colors = LANE_COLORS[lane.type]
  const isNegated = lane.negated
  const badgeLabel = isNegated ? `${lane.type} (NOT)` : lane.type
  const laneHeight = isMobile ? 40 : 32
  const badgeFontSize = isMobile ? 10 : 9

  // Background: view = surface, edit = paper
  const bg = editMode ? '#F2F0EB' : '#FAFAF7'
  const borderColor = isEmpty ? '#D97706' : isNegated ? '#DC2626' : colors.border

  return (
    <div
      aria-label={`Lane: ${lane.type} ${lane.target}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        height: laneHeight,
        padding: isMobile ? '8px 12px' : '6px 10px',
        background: bg,
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: 4,
        animation: 'pv2-lane-enter 200ms ease-out',
        boxSizing: 'border-box',
      }}
    >
      {/* Drag handle — edit mode only */}
      {editMode && (
        <div
          data-testid="lane-drag-handle"
          {...dragHandleProps}
          style={{
            cursor: 'grab',
            color: '#D4D0C8',
            fontSize: 14,
            lineHeight: 1,
            flexShrink: 0,
            width: isMobile ? 20 : 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
            ...(dragHandleProps?.style as React.CSSProperties | undefined),
          }}
        >
          ⠿
        </div>
      )}

      {/* Type badge */}
      <div
        role="status"
        aria-label={`Lane type: ${badgeLabel}`}
        style={{
          background: isNegated ? 'rgba(220,38,38,0.10)' : colors.badgeBg,
          color: isNegated ? '#DC2626' : colors.badgeText,
          padding: '2px 6px',
          borderRadius: 4,
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: badgeFontSize,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        {badgeLabel}
      </div>

      {/* Field value */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
        {editMode ? (
          <input
            type="text"
            value={lane.target}
            onChange={e => onTargetChange?.(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: isMobile ? 14 : 13,
              color: '#1A1916',
              fontFamily: 'Inter, -apple-system, sans-serif',
              padding: 0,
              minWidth: 0,
            }}
          />
        ) : (
          <span style={{
            fontSize: isMobile ? 14 : 13,
            color: '#1A1916',
            fontFamily: 'Inter, -apple-system, sans-serif',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: isNegated ? 'line-through' : 'none',
            opacity: isNegated ? 0.7 : 1,
          }}>
            {lane.target}
          </span>
        )}

        {/* Unassessable warning icons */}
        {(lane.unassessable ?? []).length > 0 && (
          <span
            title={`Darwin can't verify subjective qualities like '${lane.unassessable!.join(', ')}'`}
            style={{ flexShrink: 0, fontSize: 12 }}
          >
            ⚠️
          </span>
        )}

        {/* Empty lane warning */}
        {isEmpty && (
          <span title="This lane is empty and will be ignored on submit" style={{ flexShrink: 0, fontSize: 12 }}>
            ⚠
          </span>
        )}
      </div>

      {/* Delete button — edit mode only */}
      {editMode && (
        <button
          onClick={onDelete}
          aria-label="Delete lane"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#7A7669',
            fontSize: 14,
            lineHeight: 1,
            padding: '0 2px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? 32 : 24,
            height: isMobile ? 40 : 32,
          }}
        >
          ×
        </button>
      )}
    </div>
  )
}
